import ScoreUtils from "../utils/ScoreUtils.js";
import MapExploration from "../utils/MapExploration.js";
import AccountSync from "../services/AccountSync.js";
import StorageService from "../services/StorageService.js";
import DailyChallenge from "../games/DailyChallenge.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";
import { sanitizeRun } from "../services/syncMerge.js";
import { formatDuration } from "../utils/TimeUtils.js";

/**
 * Progression personnelle : statistiques globales, exploration de la carte et
 * scores par mode. Rendue dans `.profile-progress` sur /profil — elle a d'abord
 * été une page (/scores), puis un onglet du classement (`#mine`), avant de
 * rejoindre le profil.
 */
export default class Scores {
    constructor() {
        this.page = document.querySelector(".profile-progress");
        if (!this.page) return;

        this.scoresContainer = this.page.querySelector(".scores-container");
        // .btn-share-all is injected into the global-stats card by renderGlobalStats().
        this.init();
    }

    init() {
        this.globalStatsContainer = this.page.querySelector(".global-stats-container");
        this.explorationContainer = this.page.querySelector(".exploration-container");
        this.renderGlobalStats();
        this.renderExploration();
        this.renderScores();

        // The global share button now lives inside the global-stats card and is
        // wired in renderGlobalStats() (rebuilt on every render).
    }

    /**
     * Every gameId writing per-mode state under `{gameId}.{suffix}` keys.
     * Kept as a list × `RESET_KEY_SUFFIXES` rather than hand-written key names:
     * the flat list had drifted (no `.bestRun` anywhere, speed runs limited to
     * `.best`), which left the composite score computed on a cleared record.
     */
    static RESET_GAME_IDS = [
        "flagnerd", "flagnerdhard", "flagnerdtricky", "flagnerd.speedrun",
        "capitalnerd", "capitalnerdhard", "capitalnerdreverse", "capitalnerd.speedrun",
        "mapnerdclassic", "mapnerdhard",
        "countrynerd", "countrynerdhard",
        "usstatesclassic", "usstateshard", "usstateslist",
        "dailychallenge", "dailychallengehard"
    ];

    /**
     * Per-mode suffixes written by the games. `.best`, `.bestRun`, `.gamesPlayed`
     * and `.cumulativeScore` are global keys (unprefixed, see
     * `LanguageManager.GLOBAL_KEY_SUFFIXES`); the rest are language-prefixed.
     * Removing a suffix a given mode never writes is a no-op.
     */
    static RESET_KEY_SUFFIXES = [
        "best", "bestRun", "countriesleft", "current", "proposals", "lifes", "gamesPlayed", "cumulativeScore"
    ];

    resetAllProgress() {
        showConfirmation(
            window.geoNerdApp.languageManager.t("settings.confirmTitle"),
            window.geoNerdApp.languageManager.t("settings.clearAllNotice"),
            () => {
                // List of all keys to clear for BOTH languages
                const baseKeys = [];
                const pushModeKeys = gameId => {
                    Scores.RESET_KEY_SUFFIXES.forEach(suffix => baseKeys.push(`${gameId}.${suffix}`));
                };
                Scores.RESET_GAME_IDS.forEach(pushModeKeys);

                // Per-zone Map Nerd scopes (mapnerdclassic.af, mapnerdhard.am, …).
                const continents = ["af", "eu", "as", "am"];
                ["mapnerdclassic", "mapnerdhard"].forEach(base => {
                    continents.forEach(c => pushModeKeys(`${base}.${c}`));
                });

                // Explorer progression (single raw, language-agnostic key).
                MapExploration.reset();

                // Daily Challenge state lives under raw `geonerd.daily[.hard].*` keys
                // (streak, in-flight progress, dated results) — outside the
                // language-prefixed scheme, so they can't be resolved like the rest.
                // Leaving the dated key behind keeps the day flagged "already played".
                DailyChallenge.storedKeys().forEach(key => localStorage.removeItem(key));

                const languages = ["fr", "en"];
                baseKeys.forEach(key => {
                    languages.forEach(lang => {
                        localStorage.removeItem(`${lang}.${key}`);
                    });
                    // Also clear legacy non-prefixed keys
                    localStorage.removeItem(key);
                });

                // Specialized clear for Country Nerd (both classic and hard modes)
                const letters = window.geoNerdApp?.letters || "abcdefghijklmnopqrstuvwxyz".split("");
                letters.forEach(letter => {
                    ["countrynerd", "countrynerdhard"].forEach(base => {
                        // `letterRun` (le résultat mesuré) et ses compteurs de travail
                        // comptent autant que la progression : les oublier laisserait un
                        // ancien résultat survivre au reset, et il ne serait remplacé que
                        // par un meilleur : le joueur resterait bloqué dessus.
                        ["letter", "progress", "letterRun", "letterStart", "letterFaults"].forEach(kind => {
                            languages.forEach(lang => {
                                localStorage.removeItem(`${lang}.${base}.${kind}.${letter}`);
                            });
                            localStorage.removeItem(`${base}.${kind}.${letter}`);
                        });
                    });
                });

                // Le merge de push() ne peut qu'accroître les valeurs (max/union) :
                // un reset doit passer par le endpoint de suppression dédié.
                AccountSync.deleteRemote();

                this.renderScores(); // Refresh the list
            }
        );
    }

    getGameData() {
        return [
            {
                id: "flag-nerd",
                name: window.geoNerdApp.languageManager.t("home.flagNerd"),
                icon: "/img/flag.svg",
                pageHash: "flag-nerd-classic",
                modes: [
                    { label: window.geoNerdApp.languageManager.t("home.classic"), key: "flagnerd.best", class: "classic", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.hard"), key: "flagnerdhard.best", class: "hard", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.tricky"), key: "flagnerdtricky.best", class: "tricky", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.speedRun"), key: "flagnerd.speedrun.best", class: "speed-run", hasStats: true }
                ]
            },
            {
                id: "map-nerd",
                name: window.geoNerdApp.languageManager.t("home.mapNerd"),
                icon: "/img/map.svg",
                pageHash: "map-nerd-classic",
                modes: [
                    { label: window.geoNerdApp.languageManager.t("home.classic"), key: "mapnerdclassic.best", class: "classic", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.hard"), key: "mapnerdhard.best", class: "hard", hasStats: true }
                ]
            },
            {
                id: "capital-nerd",
                name: window.geoNerdApp.languageManager.t("home.capitalNerd"),
                icon: "/img/home-pin.svg",
                pageHash: "capital-nerd-classic",
                modes: [
                    { label: window.geoNerdApp.languageManager.t("home.classic"), key: "capitalnerd.best", class: "classic", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.hard"), key: "capitalnerdhard.best", class: "hard", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.speedRun"), key: "capitalnerd.speedrun.best", class: "speed-run", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.reverse"), key: "capitalnerdreverse.best", class: "reverse", hasStats: true }
                ]
            },
            {
                id: "country-nerd",
                name: window.geoNerdApp.languageManager.t("home.countryNerd"),
                icon: "/img/list.svg",
                pageHash: "country-nerd",
                modes: [
                    {
                        label: window.geoNerdApp.languageManager.t("home.classic"),
                        key: "countrynerd.progress",
                        class: "classic",
                        hasStats: true,
                        isCountryNerd: true
                    },
                    {
                        label: window.geoNerdApp.languageManager.t("home.hard"),
                        key: "countrynerdhard.progress",
                        class: "hard",
                        hasStats: true,
                        isCountryNerd: true
                    }
                ]
            },
            {
                id: "us-states",
                name: window.geoNerdApp.languageManager.t("home.usStates"),
                icon: "/img/us-states.svg",
                pageHash: "us-states-classic",
                modes: [
                    { label: window.geoNerdApp.languageManager.t("home.classic"), key: "usstatesclassic.best", class: "classic", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.hard"), key: "usstateshard.best", class: "hard", hasStats: true },
                    { label: window.geoNerdApp.languageManager.t("home.list"), key: "usstateslist.best", class: "list", hasStats: true }
                ]
            },
            {
                id: "daily-challenge",
                name: window.geoNerdApp.languageManager.t("home.dailyChallenge"),
                icon: "/img/flame.svg",
                pageHash: "daily-challenge",
                modes: [
                    { label: window.geoNerdApp.languageManager.t("home.classic"), key: "dailychallenge.best", class: "daily", hasStats: true, isDaily: true },
                    { label: window.geoNerdApp.languageManager.t("home.hard"), key: "dailychallengehard.best", class: "daily-hard", hasStats: true, isDaily: true }
                ]
            }
        ];
    }

    getCountryNerdStats(baseKey = "countrynerd") {
        let foundLetters = 0;
        const letters = window.geoNerdApp?.letters || "abcdefghijklmnopqrstuvwxyz".split("");
        letters.forEach(l => {
            if (localStorage.getItem(window.geoNerdApp.languageManager.getStorageKey(`${baseKey}.letter.${l}`)) === "true") {
                foundLetters++;
            }
        });
        return {
            foundLetters,
            totalLetters: letters.length,
            gamesPlayed: 0, // Not tracked yet for Country Nerd
            cumulativeScore: 0
        };
    }

    /**
     * Fautes/temps totalisés sur les lettres complétées, pour enrichir le partage
     * comme `extractEntry()` le fait côté serveur (`server/src/routes/leaderboard.js`)
     * — mais sur la seule langue courante : `getCountryNerdStats()` ci-dessus ne
     * compare pas non plus fr/en. Une lettre bouclée avant l'existence du chrono
     * (`letterRun` absent) rend le total incomparable : il est déclaré non mesuré
     * (`null`) plutôt que de n'additionner qu'une partie des lettres.
     */
    getCountryNerdRunTotals(baseKey) {
        const letters = window.geoNerdApp?.letters || "abcdefghijklmnopqrstuvwxyz".split("");
        let errors = 0, time = 0, measured = true, anyDone = false;
        letters.forEach(l => {
            const done = localStorage.getItem(window.geoNerdApp.languageManager.getStorageKey(`${baseKey}.letter.${l}`)) === "true";
            if (!done) return;
            anyDone = true;
            const run = StorageService.load(`${baseKey}.letterRun.${l}`);
            if (!run || typeof run.time !== "number") { measured = false; return; }
            errors += run.errors ?? 0;
            time += run.time;
        });
        return anyDone && measured ? { errors, time } : null;
    }

    /**
     * Vies (ou fautes pour US States Liste) + temps du meilleur run, mêmes infos
     * que le partage en fin de partie (`BaseGame.initShare()` / `UsStatesList.initShare()`)
     * — sans ça un score seul sur `/profil` ne disait pas s'il venait d'une
     * victoire nette ou d'une dernière vie perdue. `bestRun` est une clé globale
     * (`LanguageManager.GLOBAL_KEY_SUFFIXES`), commune aux deux langues. Un run
     * sans chrono (`duration` absent, d'avant son existence) reste silencieusement
     * sans enrichissement, comme au classement.
     */
    formatBestRunText(baseKey, { lives }) {
        const run = sanitizeRun(StorageService.load(`${baseKey}.bestRun`));
        if (!run || run.duration === null || run.duration === undefined) return "";

        const lang = window.geoNerdApp.languageManager;
        const errors = run.errors ?? 0;
        const middle = lives
            ? (errors < 3 ? "❤️".repeat(Math.max(0, 3 - errors)) : "💔")
            : `✗${errors} ${lang.t("leaderboard.errors").toLowerCase()}`;
        return ` · ${middle} · ⏱${formatDuration(run.duration)}`;
    }

    /**
     * Score composite (0-1000), MIROIR de `COMPOSITE_GAMES`/`geonerdcomposite` dans
     * `server/src/routes/leaderboard.js` (l'onglet "Score global" du classement).
     * Garder les deux en phase si l'un des deux change.
     */
    static COMPOSITE_GAMES = [
        { id: "flagnerd", max: 193, weight: 1 },
        { id: "flagnerdhard", max: 193, weight: 2 },
        { id: "flagnerd.speedrun", max: 40, weight: 1 },
        { id: "mapnerdclassic", max: 170, weight: 1 },
        { id: "mapnerdhard", max: 170, weight: 2 },
        { id: "capitalnerd", max: 193, weight: 1 },
        { id: "capitalnerdhard", max: 193, weight: 2 },
        { id: "capitalnerd.speedrun", max: 40, weight: 1 },
        { id: "capitalnerdreverse", max: 193, weight: 1 },
        { id: "countrynerd", max: 24, weight: 1 },
        { id: "countrynerdhard", max: 24, weight: 2 },
        { id: "usstatesclassic", max: 50, weight: 1 },
        { id: "usstateshard", max: 50, weight: 2 },
        { id: "usstateslist", max: 50, weight: 1 }
        // +2 pour l'exploration → total = 21
    ];
    static COMPOSITE_TOTAL_WEIGHT = Scores.COMPOSITE_GAMES.reduce((s, g) => s + g.weight, 0) + 2;
    static COUNTRY_LETTERS = "abcdefghijklmnopqrstuvyz"; // 24 lettres (pas w ni x)

    getGameBest(gameId) {
        if (gameId === "countrynerd" || gameId === "countrynerdhard") {
            let best = 0;
            ["fr", "en"].forEach(lang => {
                let count = 0;
                for (const letter of Scores.COUNTRY_LETTERS) {
                    if (localStorage.getItem(`${lang}.${gameId}.letter.${letter}`) === "true") count++;
                }
                if (count > best) best = count;
            });
            return best;
        }
        const runRaw = localStorage.getItem(`${gameId}.bestRun`);
        if (runRaw) {
            try {
                const run = JSON.parse(runRaw);
                if (run && typeof run.score === "number" && run.score > 0) return run.score;
            } catch {}
        }
        return parseInt(localStorage.getItem(`${gameId}.best`) ?? "0") || 0;
    }

    getGlobalScore() {
        let weighted = 0;
        Scores.COMPOSITE_GAMES.forEach(g => {
            const best = this.getGameBest(g.id);
            if (best > 0) weighted += Math.min(best / g.max, 1) * g.weight;
        });
        const discovered = MapExploration.getDiscovered().size;
        if (discovered > 0) weighted += Math.min(discovered / 170, 1) * 2;
        return Math.round(weighted / Scores.COMPOSITE_TOTAL_WEIGHT * 1000);
    }

    getGlobalStats() {
        const lang = window.geoNerdApp.languageManager;
        const modes = [
            { label: "Flag Nerd Classic", key: "flagnerd", max: () => window.geoNerdApp.countries.length },
            { label: "Flag Nerd Hard", key: "flagnerdhard", max: () => window.geoNerdApp.countries.length },
            { label: "Capital Nerd Classic", key: "capitalnerd", max: () => window.geoNerdApp.countries.length },
            { label: "Capital Nerd Hard", key: "capitalnerdhard", max: () => window.geoNerdApp.countries.length },
            { label: "Map Nerd Classic", key: "mapnerdclassic", max: () => window.geoNerdApp.easyCountries.length },
            { label: "Map Nerd Hard", key: "mapnerdhard", max: () => window.geoNerdApp.easyCountries.length },
            { label: "US States Classic", key: "usstatesclassic", max: () => window.geoNerdApp.usStates.length },
            { label: "US States Hard", key: "usstateshard", max: () => window.geoNerdApp.usStates.length },
            { label: "US States List", key: "usstateslist", max: () => window.geoNerdApp.usStates.length },
            { label: `${lang.t("home.dailyChallenge")} Classic`, key: "dailychallenge", max: () => 20 },
            { label: `${lang.t("home.dailyChallenge")} Hard`, key: "dailychallengehard", max: () => 20 }
        ];

        let totalGames = 0;
        let totalScore = 0;
        let totalMaxScore = 0;
        let bestMode = null;
        let worstMode = null;
        let bestRate = -1;
        let worstRate = 2;

        modes.forEach(m => {
            const stats = ScoreUtils.getStats(m.key);
            if (stats.gamesPlayed === 0) return;

            totalGames += stats.gamesPlayed;
            totalScore += stats.cumulativeScore;
            totalMaxScore += stats.gamesPlayed * m.max();

            const rate = stats.cumulativeScore / (stats.gamesPlayed * m.max());
            if (rate > bestRate) {
                bestRate = rate;
                bestMode = m.label;
            }
            if (rate < worstRate) {
                worstRate = rate;
                worstMode = m.label;
            }
        });

        const successRate = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;

        return { totalGames, successRate, bestMode, worstMode };
    }

    renderGlobalStats() {
        if (!this.globalStatsContainer) return;

        const stats = this.getGlobalStats();
        const globalScore = this.getGlobalScore();
        const lang = window.geoNerdApp.languageManager;

        // Always render the card (with placeholders when there's no data yet) so it
        // stays present alongside the exploration card.
        this.globalStatsContainer.innerHTML = `
            <div class="global-stats-card">
                <h2>${lang.t("scores.globalStats")}</h2>
                <div class="global-stats-grid">
                    <div class="global-stat">
                        <span class="global-stat-value">${stats.totalGames}</span>
                        <span class="global-stat-label">${lang.t("scores.totalGamesPlayed")}</span>
                    </div>
                    <div class="global-stat best">
                        <span class="global-stat-value">${stats.bestMode || "-"}</span>
                        <span class="global-stat-label">${lang.t("scores.bestMode")}</span>
                    </div>
                </div>
                <div class="stat-progress global-score-progress">
                    <span class="stat-label">${lang.t("leaderboard.composite")}</span>
                    <div class="progress-bar-container">
                        <div class="progress-bar" style="width: ${globalScore / 10}%"></div>
                    </div>
                    <span class="stat-value">${globalScore}/1000</span>
                </div>
                <button type="button" class="btn btn-share btn-share-all">
                    <span class="share-icon"></span>
                    <span class="share-text">${lang.t("scores.shareGlobal")}</span>
                </button>
            </div>
        `;

        // Re-wire on every render: the card (and its button) is rebuilt whenever
        // scores change (e.g. after a reset).
        this.shareAllBtn = this.globalStatsContainer.querySelector(".btn-share-all");
        if (this.shareAllBtn) {
            this.shareAllBtn.addEventListener("click", () => this.shareAllScores());
        }
    }

    /** Zone order for the exploration panel. */
    static EXPLORATION_CONTINENTS = ["af", "eu", "as", "am"];

    /**
     * Render the "explorer" progression: per-continent + world discovery bars,
     * the current world rank, and which continent to focus on next.
     */
    renderExploration() {
        if (!this.explorationContainer) return;
        const lang = window.geoNerdApp.languageManager;
        const easy = window.geoNerdApp.easyCountries || [];
        const byContinent = window.geoNerdApp.easyCountriesByContinent || {};

        const world = MapExploration.stats(easy.map(c => c.code));
        if (world.total === 0) {
            this.explorationContainer.innerHTML = "";
            return;
        }

        const rows = [];
        let worst = null;
        Scores.EXPLORATION_CONTINENTS.forEach(code => {
            const pool = byContinent[code];
            if (!pool || pool.length === 0) return;
            const stats = MapExploration.stats(pool.map(c => c.code));
            const label = lang.t(`mapExploration.continents.${code}`);
            rows.push({ code, label, ...stats });
            if (!worst || stats.percent < worst.percent) worst = { label, percent: stats.percent };
        });

        const rankLabel = lang.t(`mapExploration.ranks.${MapExploration.rankFor(world.percent)}`);

        const bar = (label, found, total, percent, extraClass = "") => `
            <div class="exploration-row ${extraClass}">
                <span class="exploration-label">${label}</span>
                <div class="exploration-bar"><div class="exploration-bar-fill" style="width: ${percent}%"></div></div>
                <span class="exploration-value">${found}/${total}</span>
            </div>
        `;

        this.explorationContainer.innerHTML = `
            <div class="exploration-card">
                <h2>${lang.t("mapExploration.title")}</h2>
                <div class="exploration-rank">
                    <span class="exploration-rank-label">${lang.t("mapExploration.rankLabel")}</span>
                    <span class="exploration-rank-value">${rankLabel}</span>
                    <span class="exploration-rank-percent">${world.percent}%</span>
                </div>
                ${bar(lang.t("mapExploration.worldLabel"), world.found, world.total, world.percent, "world")}
                <div class="exploration-continents">
                    ${rows.map(r => bar(r.label, r.found, r.total, r.percent)).join("")}
                </div>
                ${worst && worst.percent < 100 ? `<p class="exploration-focus">${lang.t("mapExploration.focusContinent")} : <strong>${worst.label}</strong></p>` : ""}
            </div>
        `;
    }

    renderScores() {
        const games = this.getGameData();
        this.scoresContainer.innerHTML = "";
        this.renderGlobalStats();
        this.renderExploration();

        games.forEach(game => {
            const card = document.createElement("div");
            card.className = "game-score-card";

            let modesHtml = "";
            game.modes.forEach(mode => {
                const score = mode.value !== undefined ? mode.value : localStorage.getItem(window.geoNerdApp.languageManager.getStorageKey(mode.key));
                const displayScore = score !== null ? score : `<span class="no-score">${window.geoNerdApp.languageManager.t("scores.noScore")}</span>`;

                let modeHtml = "";
                if (mode.isCountryNerd) {
                    const stats = this.getCountryNerdStats(mode.key.replace(".progress", ""));
                    modeHtml = ScoreUtils.renderModeStats(
                        mode.label,
                        stats.foundLetters,
                        stats.totalLetters,
                        stats.gamesPlayed,
                        stats.cumulativeScore,
                        mode.class,
                        mode.key,
                        game.id,
                        { hideGamesPlayed: true, hideAverage: true, alwaysShow: true }
                    );
                } else if (mode.isDaily) {
                    const baseKey = mode.key.replace(".best", "");
                    const stats = ScoreUtils.getStats(baseKey);
                    const maxPossible = 20;
                    const currentProgress = score !== null ? parseInt(score) : null;

                    modeHtml = ScoreUtils.renderModeStats(
                        mode.label,
                        currentProgress,
                        maxPossible,
                        stats.gamesPlayed,
                        stats.cumulativeScore,
                        mode.class,
                        mode.hasStats ? mode.key : null,
                        game.id
                    );
                } else {
                    const baseKey = mode.key.replace(".best", "");
                    const stats = ScoreUtils.getStats(baseKey);
                    const isMapNerd = game.id === "map-nerd";
                    const isUsStates = game.id === "us-states";
                    const countriesList = isMapNerd ? window.geoNerdApp.easyCountries
                        : isUsStates ? window.geoNerdApp.usStates
                        : window.geoNerdApp.countries;
                    const defaultMax = isMapNerd ? 170 : isUsStates ? 50 : 193;
                    const maxPossible = countriesList && countriesList.length > 0 ? countriesList.length : defaultMax;
                    const currentProgress = score !== null ? parseInt(score) : null;

                    modeHtml = ScoreUtils.renderModeStats(
                        mode.label,
                        currentProgress,
                        maxPossible,
                        stats.gamesPlayed,
                        stats.cumulativeScore,
                        mode.class,
                        mode.hasStats ? mode.key : null,
                        game.id
                    );
                }
                modesHtml += modeHtml;
            });

            card.innerHTML = `
				<div class="game-header">
					<img class="game-icon" src="${game.icon}" alt="">
					<h2>${game.name}</h2>
				</div>
				<div class="modes-list">
					${modesHtml}
				</div>
                <div class="card-footer">
                    <button type="button" class="btn btn-share" data-game-id="${game.id}">
                        <span class="share-icon"></span>
                        <span class="share-text">${window.geoNerdApp.languageManager.t("scores.share")}</span>
                    </button>
                </div>
			`;

            const shareBtn = card.querySelector(".btn-share");
            shareBtn.addEventListener("click", () => this.shareGameScore(game));

            this.scoresContainer.appendChild(card);
        });

        this.scoresContainer.querySelectorAll('.mode-item.collapsible').forEach(item => {
            item.addEventListener('click', (e) => {
                if (e.target.closest('.btn-reset-mode')) return;
                item.classList.toggle('expanded');
            });
        });

        this.scoresContainer.querySelectorAll('.btn-reset-mode').forEach(resetBtn => {
            resetBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const gameId = resetBtn.dataset.gameId;
                const modeKey = resetBtn.dataset.modeKey;

                showConfirmation(
                    window.geoNerdApp.languageManager.t("settings.confirmTitle"),
                    window.geoNerdApp.languageManager.t("settings.confirmMessage"),
                    () => this.resetIndividualProgress(gameId, modeKey)
                );
            });
        });

        this.renderResetCard();
    }

    resetIndividualProgress(gameId, modeKey) {
        const baseKey = modeKey.replace(".best", "").replace(".progress", "");
        const keysToRemove = [modeKey, ...Scores.RESET_KEY_SUFFIXES.map(suffix => `${baseKey}.${suffix}`)];

        // Specific Country Nerd keys — baseKey is the mode (countrynerd / countrynerdhard)
        if (gameId === "country-nerd") {
            const letters = window.geoNerdApp?.letters || "abcdefghijklmnopqrstuvwxyz".split("");
            letters.forEach(l => {
                // Idem `resetAllProgress()` : le résultat mesuré de la lettre part avec
                // sa progression, sinon il survit au reset et ne peut plus être battu.
                ["letter", "progress", "letterRun", "letterStart", "letterFaults"].forEach(kind => {
                    keysToRemove.push(`${baseKey}.${kind}.${l}`);
                });
            });
        }

        // Raw Daily Challenge keys (streak, in-flight progress, dated results): they
        // are language-agnostic, so they bypass getStorageKey() — and without them
        // the day stays flagged "already played" after a reset.
        const rawKeys = gameId === "daily-challenge"
            ? DailyChallenge.storedKeys(baseKey === "dailychallengehard" ? "hard" : "classic")
            : [];

        const resolvedKeys = [...new Set([
            ...keysToRemove.map(key => window.geoNerdApp.languageManager.getStorageKey(key)),
            ...rawKeys
        ])];
        resolvedKeys.forEach(key => localStorage.removeItem(key));

        // Le merge de push() ne peut qu'accroître les valeurs (max/union) :
        // un reset doit passer par le endpoint de suppression dédié.
        AccountSync.deleteRemote(resolvedKeys);

        this.renderScores();
    }

    renderResetCard() {
        const resetCard = document.createElement("div");
        resetCard.className = "game-score-card reset-card reset-card-inverse";
        resetCard.innerHTML = `
            <div class="game-header">
                <img class="game-icon" src="/img/restart.svg" alt="">
                <h2 data-i18n="settings.clearData">${window.geoNerdApp.languageManager.t("settings.clearData")}</h2>
            </div>
            <p style="font-size: 0.9rem; margin-top: 0.5rem; opacity: 0.9;" data-i18n="settings.clearAllNotice">${window.geoNerdApp.languageManager.t("settings.clearAllNotice")}</p>
            <div class="card-footer">
                <button type="button" class="btn btn-white btn-reset-all" data-i18n="settings.reset">
                    ${window.geoNerdApp.languageManager.t("settings.reset")}
                </button>
            </div>
        `;
        this.scoresContainer.appendChild(resetCard);

        const resetBtn = resetCard.querySelector(".btn-reset-all");
        if (resetBtn) {
            resetBtn.addEventListener("click", () => this.resetAllProgress());
        }
    }

    formatModeScore(mode, gameId) {
        let scoreValue = mode.value !== undefined ? mode.value : localStorage.getItem(window.geoNerdApp.languageManager.getStorageKey(mode.key));
        let stats = null;

        if (mode.isCountryNerd) {
            stats = this.getCountryNerdStats(mode.key.replace(".progress", ""));
            if (stats.foundLetters > 0) scoreValue = stats.foundLetters;
        }

        if (scoreValue === null) return null;

        let emoji = "🟦";
        if (mode.class === "hard") emoji = "🟥";
        if (mode.class === "tricky") emoji = "🟨";
        if (mode.class === "speed-run") emoji = "🟪";
        if (mode.class === "daily") emoji = "🟧";
        if (mode.class === "daily-hard") emoji = "🟧";

        let scoreDisplay = scoreValue;
        let runText = "";
        if (mode.class !== "speed-run") {
            if (mode.isCountryNerd) {
                scoreDisplay = `${stats.foundLetters}/${stats.totalLetters}`;
                const baseKey = mode.key.replace(".progress", "");
                const totals = stats.foundLetters > 0 ? this.getCountryNerdRunTotals(baseKey) : null;
                if (totals) {
                    const lang = window.geoNerdApp.languageManager;
                    runText = ` · ✗${totals.errors} ${lang.t("leaderboard.errors").toLowerCase()} · ⏱${formatDuration(totals.time)}`;
                }
            } else if (mode.isDaily) {
                scoreDisplay = `${scoreValue}/20`;
            } else if (gameId === "map-nerd") {
                scoreDisplay = `${scoreValue}/${window.geoNerdApp.easyCountries.length}`;
                runText = this.formatBestRunText(mode.key.replace(".best", ""), { lives: true });
            } else if (gameId === "us-states") {
                scoreDisplay = `${scoreValue}/${window.geoNerdApp.usStates.length}`;
                runText = this.formatBestRunText(mode.key.replace(".best", ""), { lives: mode.class !== "list" });
            } else {
                scoreDisplay = `${scoreValue}/${window.geoNerdApp.countries.length}`;
                runText = this.formatBestRunText(mode.key.replace(".best", ""), { lives: true });
            }
        }

        return `${emoji} ${mode.label}: ${scoreDisplay}${runText}`;
    }

    copyToClipboard(text, btn) {
        navigator.clipboard.writeText(text).then(() => {
            const textEl = btn.querySelector(".share-text");
            const originalText = textEl.textContent;
            textEl.textContent = window.geoNerdApp.languageManager.t("scores.copied");
            btn.classList.add("copied");
            setTimeout(() => {
                textEl.textContent = originalText;
                btn.classList.remove("copied");
            }, 2000);
        }).catch(() => {
            const textEl = btn.querySelector(".share-text");
            const originalText = textEl.textContent;
            textEl.textContent = window.geoNerdApp.languageManager.t("scores.copyFailed");
            setTimeout(() => {
                textEl.textContent = originalText;
            }, 2000);
        });
    }

    shareAllScores() {
        const lang = window.geoNerdApp.languageManager;
        let text = `#GeoNerd | ${lang.t("scores.myRecords")} 🌍\n\n`;

        this.getGameData().forEach(game => {
            let gameText = `*${game.name}*\n`;
            let hasScores = false;

            game.modes.forEach(mode => {
                const line = this.formatModeScore(mode, game.id);
                if (line) {
                    gameText += line + "\n";
                    hasScores = true;
                }
            });

            if (hasScores) text += gameText + "\n";
        });

        // Lien vers la home (pas `/profil`, qui n'a de sens que connecté) :
        // le destinataire n'a rien à voir sur mon propre profil, seulement
        // à découvrir le jeu.
        const langPrefix = lang.currentLang === "en" ? "/en/" : "/";
        text += `https://geo-nerd.com${langPrefix}`;
        this.copyToClipboard(text, this.shareAllBtn);
    }

    shareGameScore(game) {
        const lang = window.geoNerdApp.languageManager;
        let text = `#GeoNerd | #${game.name.replace(/\s+/g, '')} 🌍\n\n`;

        game.modes.forEach(mode => {
            const line = this.formatModeScore(mode, game.id);
            if (line) text += line + "\n";
        });

        // Même construction que `GeoNerdApp.shareResult()` : les slugs de jeu
        // sont identiques FR/EN (absents de `PAGE_SLUG_EN`), seul le préfixe change.
        const langPrefix = lang.currentLang === "en" ? "/en" : "";
        text += `\nhttps://geo-nerd.com${langPrefix}${game.pageHash ? "/" + game.pageHash : ""}`;
        const btn = this.page.querySelector(`.btn-share[data-game-id="${game.id}"]`);
        this.copyToClipboard(text, btn);
    }
}
